<script setup>
import { ref, computed, watch, onMounted, h } from 'vue';
import { RouterLink } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { formatTime } from '@chatwoot/utils';

// Components
import Table from 'dashboard/components/table/Table.vue';
import Pagination from 'dashboard/components/table/Pagination.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import V4Button from 'dashboard/components-next/button/Button.vue';
import WootDateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';

import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';

const props = defineProps({
  agentId: {
    type: [Number, String],
    required: true,
  },
  inboxes: {
    type: Array,
    default: () => [],
  },
});

const { t } = useI18n();
const store = useStore();

// Filter states
const dateRange = ref([
  new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // 7 days ago
  new Date(),
]);
const selectedMetric = ref('resolution');
const thresholdOperator = ref('gt');
const thresholdValue = ref(120);
const thresholdMax = ref(300);
const selectedInboxId = ref('');
const currentPage = ref(1);
const businessHours = ref(false);

// Metric options
const metricOptions = [
  { id: 'resolution', label: 'Resolution Time' },
  { id: 'first_response', label: 'First Response Time' },
  { id: 'customer_waiting', label: 'Customer Waiting Time' },
];

const operatorOptions = [
  { id: 'gt', label: '>' },
  { id: 'gte', label: '>=' },
  { id: 'lt', label: '<' },
  { id: 'lte', label: '<=' },
  { id: 'between', label: 'Between' },
];

// Getters
const agentSessions = computed(
  () => store.getters['summaryReports/getAgentSessions']
);

const uiFlags = computed(() => store.getters['summaryReports/getUIFlags']);

const isLoading = computed(() => uiFlags.value.isFetchingAgentSessions);

const sessions = computed(() => agentSessions.value?.sessions || []);
const pagination = computed(
  () =>
    agentSessions.value?.pagination || {
      page: 1,
      perPage: 50,
      totalCount: 0,
      totalPages: 0,
    }
);

// Table data
const tableData = computed(() => {
  return sessions.value.map(session => ({
    id: session.id,
    sessionStartAt: session.sessionStartAt,
    conversationId: session.conversationId,
    conversationDisplayId: session.conversationDisplayId,
    contactName: session.contactName || 'Unknown',
    inboxName: session.inboxName || 'Unknown',
    inboxChannelType: session.inboxChannelType,
    firstResponseSeconds: session.firstResponseSeconds || 0,
    customerWaitingSeconds: session.customerWaitingSeconds || 0,
    resolutionSeconds: session.resolutionSeconds || 0,
    status: session.status,
  }));
});

// Format time helper
const formatSeconds = seconds => {
  if (!seconds || seconds === 0) return '—';
  return formatTime(seconds);
};

// Format date helper
const formatDate = dateString => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Check if value exceeds threshold
const exceedsThreshold = value => {
  const threshold = thresholdValue.value;
  switch (thresholdOperator.value) {
    case 'gt':
      return value > threshold;
    case 'gte':
      return value >= threshold;
    case 'lt':
      return value < threshold;
    case 'lte':
      return value <= threshold;
    case 'between':
      return value >= threshold && value <= thresholdMax.value;
    default:
      return false;
  }
};

// Table columns
const columnHelper = createColumnHelper();

const columns = computed(() => [
  columnHelper.accessor('sessionStartAt', {
    header: t('AGENT_REPORTS.SESSIONS.TABLE.SESSION_START'),
    width: 150,
    cell: cellProps =>
      h(
        'span',
        { class: 'text-n-slate-12 text-sm' },
        formatDate(cellProps.getValue())
      ),
  }),
  columnHelper.accessor('conversationDisplayId', {
    header: t('AGENT_REPORTS.SESSIONS.TABLE.CONVERSATION'),
    width: 200,
    cell: cellProps => {
      const row = cellProps.row.original;
      return h('div', { class: 'flex flex-col' }, [
        h(
          RouterLink,
          {
            to: {
              name: 'inbox_conversation',
              params: { conversation_id: row.conversationId },
            },
            class: 'text-woot-500 hover:underline font-medium',
          },
          () => `#${row.conversationDisplayId}`
        ),
        h('span', { class: 'text-n-slate-11 text-xs' }, row.contactName),
        h('span', { class: 'text-n-slate-10 text-xs' }, row.inboxName),
      ]);
    },
  }),
  columnHelper.accessor('firstResponseSeconds', {
    header: t('AGENT_REPORTS.SESSIONS.TABLE.FIRST_RESPONSE'),
    width: 120,
    cell: cellProps => {
      const value = cellProps.getValue();
      const isExceeded =
        selectedMetric.value === 'first_response' && exceedsThreshold(value);
      return h(
        'span',
        {
          class: `text-sm ${isExceeded ? 'text-ruby-11 font-semibold' : 'text-n-slate-12'}`,
        },
        formatSeconds(value)
      );
    },
  }),
  columnHelper.accessor('customerWaitingSeconds', {
    header: t('AGENT_REPORTS.SESSIONS.TABLE.CUSTOMER_WAITING'),
    width: 120,
    cell: cellProps => {
      const value = cellProps.getValue();
      const isExceeded =
        selectedMetric.value === 'customer_waiting' && exceedsThreshold(value);
      return h(
        'span',
        {
          class: `text-sm ${isExceeded ? 'text-ruby-11 font-semibold' : 'text-n-slate-12'}`,
        },
        formatSeconds(value)
      );
    },
  }),
  columnHelper.accessor('resolutionSeconds', {
    header: t('AGENT_REPORTS.SESSIONS.TABLE.RESOLUTION'),
    width: 120,
    cell: cellProps => {
      const value = cellProps.getValue();
      const isExceeded =
        selectedMetric.value === 'resolution' && exceedsThreshold(value);
      return h(
        'span',
        {
          class: `text-sm ${isExceeded ? 'text-ruby-11 font-semibold' : 'text-n-slate-12'}`,
        },
        formatSeconds(value)
      );
    },
  }),
  columnHelper.accessor('conversationId', {
    header: '',
    width: 100,
    cell: cellProps => {
      const row = cellProps.row.original;
      return h(
        RouterLink,
        {
          to: {
            name: 'inbox_conversation',
            params: { conversation_id: row.conversationId },
          },
          class:
            'inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-woot-500 hover:text-woot-600 hover:bg-woot-50 rounded-lg border border-woot-200 transition-colors',
        },
        () => [
          h('span', { class: 'i-ph-arrow-square-out text-base' }),
          t('AGENT_REPORTS.SESSIONS.TABLE.OPEN_CHAT'),
        ]
      );
    },
  }),
]);

// Pagination params
const paginationParams = computed(() => ({
  pageIndex: currentPage.value - 1,
  pageSize: pagination.value.perPage || 50,
}));

// Table instance
const table = useVueTable({
  get data() {
    return tableData.value;
  },
  get columns() {
    return columns.value;
  },
  manualPagination: true,
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
  get rowCount() {
    return pagination.value.totalCount || 0;
  },
  state: {
    get pagination() {
      return paginationParams.value;
    },
  },
  onPaginationChange: updater => {
    const newPagination = updater(paginationParams.value);
    currentPage.value = newPagination.pageIndex + 1;
    fetchSessions();
  },
});

// Fetch sessions
const fetchSessions = async () => {
  const since = Math.floor(new Date(dateRange.value[0]).getTime() / 1000);
  const until = Math.floor(new Date(dateRange.value[1]).getTime() / 1000);

  await store.dispatch('summaryReports/fetchAgentSessions', {
    userId: props.agentId,
    since,
    until,
    businessHours: businessHours.value,
    metricFilter: selectedMetric.value,
    threshold: thresholdValue.value,
    thresholdMax:
      thresholdOperator.value === 'between' ? thresholdMax.value : undefined,
    thresholdOperator: thresholdOperator.value,
    inboxId: selectedInboxId.value || undefined,
    page: currentPage.value,
    perPage: 50,
  });
};

// Apply filters
const applyFilters = () => {
  currentPage.value = 1;
  fetchSessions();
};

// Handle date change
const handleDateChange = range => {
  if (Array.isArray(range) && range.length === 2) {
    dateRange.value = range;
  }
};

// Toggle business hours
const toggleBusinessHours = () => {
  businessHours.value = !businessHours.value;
};

// Initial fetch
onMounted(() => {
  fetchSessions();
});

// Watch for agent changes
watch(
  () => props.agentId,
  () => {
    currentPage.value = 1;
    fetchSessions();
  }
);
</script>

<template>
  <div class="mt-6">
    <!-- Filters -->
    <div class="mb-4 p-4 bg-n-solid-2 rounded-xl border border-n-container">
      <div class="flex flex-wrap items-end gap-4">
        <!-- Date Range -->
        <div class="flex flex-col gap-1">
          <label class="text-xs text-n-slate-11 font-medium">
            {{ t('AGENT_REPORTS.SESSIONS.FILTERS.DATE_RANGE') }}
          </label>
          <WootDateRangePicker
            show-range
            :value="dateRange"
            :confirm-text="t('REPORT.CUSTOM_DATE_RANGE.CONFIRM')"
            :placeholder="t('REPORT.CUSTOM_DATE_RANGE.PLACEHOLDER')"
            class="auto-width"
            @change="handleDateChange"
          />
        </div>

        <!-- Metric Filter -->
        <div class="flex flex-col gap-1">
          <label class="text-xs text-n-slate-11 font-medium">
            {{ t('AGENT_REPORTS.SESSIONS.FILTERS.METRIC') }}
          </label>
          <select
            v-model="selectedMetric"
            class="h-10 px-3 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12 min-w-[160px]"
          >
            <option
              v-for="metric in metricOptions"
              :key="metric.id"
              :value="metric.id"
            >
              {{ metric.label }}
            </option>
          </select>
        </div>

        <!-- Threshold Operator -->
        <div class="flex flex-col gap-1">
          <label class="text-xs text-n-slate-11 font-medium">
            {{ t('AGENT_REPORTS.SESSIONS.FILTERS.CONDITION') }}
          </label>
          <select
            v-model="thresholdOperator"
            class="h-10 px-3 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12 min-w-[100px]"
          >
            <option v-for="op in operatorOptions" :key="op.id" :value="op.id">
              {{ op.label }}
            </option>
          </select>
        </div>

        <!-- Threshold Value -->
        <div class="flex flex-col gap-1">
          <label class="text-xs text-n-slate-11 font-medium">
            {{ t('AGENT_REPORTS.SESSIONS.FILTERS.THRESHOLD') }}
          </label>
          <div class="flex items-center gap-2">
            <input
              v-model.number="thresholdValue"
              type="number"
              min="0"
              class="w-24 h-10 px-3 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
              :placeholder="t('AGENT_REPORTS.SESSIONS.FILTERS.SECONDS')"
            />
            <span
              v-if="thresholdOperator === 'between'"
              class="text-n-slate-11"
            >
              —
            </span>
            <input
              v-if="thresholdOperator === 'between'"
              v-model.number="thresholdMax"
              type="number"
              min="0"
              class="w-24 h-10 px-3 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
              :placeholder="t('AGENT_REPORTS.SESSIONS.FILTERS.SECONDS')"
            />
            <span class="text-xs text-n-slate-10">
              {{ t('AGENT_REPORTS.SESSIONS.FILTERS.SECONDS') }}
            </span>
          </div>
        </div>

        <!-- Business Hours Toggle -->
        <div class="flex flex-col gap-1">
          <label class="text-xs text-n-slate-11 font-medium">
            {{ t('AGENT_REPORTS.SESSIONS.FILTERS.BUSINESS_HOURS') }}
          </label>
          <button
            type="button"
            class="flex items-center gap-2 h-10 px-3 rounded-lg border text-sm"
            :class="
              businessHours
                ? 'bg-woot-500 text-white border-woot-500'
                : 'bg-n-alpha-1 text-n-slate-12 border-n-weak'
            "
            @click="toggleBusinessHours"
          >
            <span
              :class="businessHours ? 'i-ph-check-bold' : 'i-ph-clock'"
              class="text-base"
            />
            {{ businessHours ? 'ON' : 'OFF' }}
          </button>
        </div>

        <!-- Apply Button -->
        <V4Button
          :label="t('AGENT_REPORTS.SESSIONS.FILTERS.APPLY')"
          icon="i-ph-funnel"
          @click="applyFilters"
        />
      </div>
    </div>

    <!-- Results -->
    <div class="bg-n-solid-2 rounded-xl border border-n-container p-4">
      <!-- Header -->
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-base font-medium text-n-slate-12">
          {{
            t('AGENT_REPORTS.SESSIONS.RESULTS_TITLE', {
              count: pagination.totalCount || 0,
            })
          }}
        </h3>
        <span v-if="pagination.totalCount" class="text-sm text-n-slate-11">
          {{ t('AGENT_REPORTS.SESSIONS.SHOWING_SLOWEST') }}
        </span>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="flex items-center justify-center py-20">
        <Spinner size="large" />
      </div>

      <!-- Empty State -->
      <div
        v-else-if="!sessions.length"
        class="flex flex-col items-center justify-center py-20 text-n-slate-11"
      >
        <span class="i-ph-chart-bar text-4xl mb-3" />
        <p class="text-sm">
          {{ t('AGENT_REPORTS.SESSIONS.NO_SESSIONS') }}
        </p>
        <p class="text-xs text-n-slate-10 mt-1">
          {{ t('AGENT_REPORTS.SESSIONS.NO_SESSIONS_HINT') }}
        </p>
      </div>

      <!-- Table -->
      <template v-else>
        <Table :table="table" class="max-h-[calc(100vh-28rem)]" />
        <div v-if="pagination.totalPages > 1" class="mt-4">
          <Pagination :table="table" />
        </div>
      </template>
    </div>
  </div>
</template>
